import React from 'react';
import { User, Building, Mail, Phone, Package, Eye, Edit2, Trash2, Check, X, Briefcase, Award, Star, Medal, Crown, Clock, CheckCircle, XCircle } from 'lucide-react';

const ClientsList = ({ clients, onView, onEdit, onDelete, onViewProducts, hasPermission }) => {
    // Calculate loyalty tier based on product count
    const getLoyaltyTier = (productCount) => {
        if (productCount >= 5) return { tier: 'Platinum', color: 'purple', icon: Crown };
        if (productCount >= 3) return { tier: 'Gold', color: 'yellow', icon: Medal };
        if (productCount >= 2) return { tier: 'Silver', color: 'gray', icon: Star };
        if (productCount >= 1) return { tier: 'Bronze', color: 'orange', icon: Award };
        return null;
    };

    if (clients.length === 0) {
        return (
            <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 mb-4">No clients found</p>
                <p className="text-sm text-gray-500">Try adjusting your filters or add a new client</p>
            </div>
        );
    }

    const handleRowClick = (e, client) => {
        // Don't navigate if clicking on action buttons or products link
        if (e.target.closest('.action-button')) {
            return;
        }
        onView(client);
    };

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Client
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Tier
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Contact
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Products
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Assets
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Consent
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Approved
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                        </th>
                    </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                    {clients.map((client) => {
                        const productCount = client.productCount || client.products?.length || 0;
                        const loyaltyInfo = getLoyaltyTier(productCount);
                        const isLoyaltySubscribed = client.loyaltySubscribed !== false && productCount > 0;

                        return (
                            <tr
                                key={client.id}
                                onClick={(e) => handleRowClick(e, client)}
                                className="hover:bg-gray-50 transition cursor-pointer"
                            >
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center">
                                        <div className="flex-shrink-0 w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                                            {client.clientType === 'CORPORATE' ? (
                                                <Building className="w-5 h-5 text-gray-600" />
                                            ) : (
                                                <User className="w-5 h-5 text-gray-600" />
                                            )}
                                        </div>
                                        <div className="ml-4">
                                            <div className="text-sm font-medium text-gray-900">
                                                {client.clientType === 'CORPORATE'
                                                    ? client.companyName || client.name
                                                    : `${client.firstName || ''} ${client.lastName || ''}`.trim() || client.name}
                                            </div>
                                            <div className="text-sm text-gray-500">
                                                Code: {client.clientCode}
                                            </div>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`px-2 py-1 text-xs rounded-full ${
                                        client.clientType === 'CORPORATE'
                                            ? 'bg-purple-100 text-purple-800'
                                            : 'bg-blue-100 text-blue-800'
                                    }`}>
                                        {client.clientType}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    {isLoyaltySubscribed && loyaltyInfo ? (
                                        <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r ${
                                            loyaltyInfo.tier === 'Platinum' ? 'from-purple-100 to-purple-50 border border-purple-200' :
                                                loyaltyInfo.tier === 'Gold' ? 'from-yellow-100 to-yellow-50 border border-yellow-200' :
                                                    loyaltyInfo.tier === 'Silver' ? 'from-gray-100 to-gray-50 border border-gray-200' :
                                                        'from-orange-100 to-orange-50 border border-orange-200'
                                        }`}>
                                            <loyaltyInfo.icon className={`w-3.5 h-3.5 ${
                                                loyaltyInfo.tier === 'Platinum' ? 'text-purple-600' :
                                                    loyaltyInfo.tier === 'Gold' ? 'text-yellow-600' :
                                                        loyaltyInfo.tier === 'Silver' ? 'text-gray-600' :
                                                            'text-orange-600'
                                            }`} />
                                            <span className={`text-xs font-medium ${
                                                loyaltyInfo.tier === 'Platinum' ? 'text-purple-800' :
                                                    loyaltyInfo.tier === 'Gold' ? 'text-yellow-800' :
                                                        loyaltyInfo.tier === 'Silver' ? 'text-gray-800' :
                                                            'text-orange-800'
                                            }`}>
                                                {loyaltyInfo.tier}
                                            </span>
                                        </div>
                                    ) : (
                                        <span className="text-xs text-gray-400">-</span>
                                    )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-900">
                                        <div className="flex items-center gap-1">
                                            <Mail className="w-3 h-3 text-gray-400" />
                                            {client.email}
                                        </div>
                                        <div className="flex items-center gap-1 mt-1">
                                            <Phone className="w-3 h-3 text-gray-400" />
                                            {client.phoneNumber}
                                        </div>
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onViewProducts(client);
                                        }}
                                        className="action-button flex items-center gap-1 text-blue-600 hover:text-blue-700"
                                    >
                                        <Package className="w-4 h-4" />
                                        <span>{productCount}</span>
                                    </button>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            // You'll need to pass navigate as a prop or use it from context
                                            // navigate(`/clients/${client.id}?tab=assets`);
                                        }}
                                        className="action-button flex items-center gap-1 text-purple-600 hover:text-purple-700"
                                    >
                                        <Briefcase className="w-4 h-4" />
                                        <span>{client.assetCount || 0}</span>
                                    </button>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    {client.crossSellingConsent ? (
                                        <span className="flex items-center gap-1 text-green-600">
                                            <Check className="w-4 h-4" />
                                            <span className="text-sm">Given</span>
                                        </span>
                                    ) : (
                                        <span className="flex items-center gap-1 text-gray-400">
                                            <X className="w-4 h-4" />
                                            <span className="text-sm">Not Given</span>
                                        </span>
                                    )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    {client.approvalStatus === 'APPROVED' ? (
                                        <div>
                                            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                                                <CheckCircle className="w-3 h-3" />
                                                Approved
                                            </span>
                                            {client.approvedBy && (
                                                <p className="text-xs text-gray-400 mt-1">By: {client.approvedBy}</p>
                                            )}
                                        </div>
                                    ) : client.approvalStatus === 'REJECTED' ? (
                                        <div>
                                            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">
                                                <XCircle className="w-3 h-3" />
                                                Rejected
                                            </span>
                                            {client.rejectionReason && (
                                                <p className="text-xs text-gray-400 mt-1 max-w-xs truncate">{client.rejectionReason}</p>
                                            )}
                                        </div>
                                    ) : (
                                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800">
                                            <Clock className="w-3 h-3" />
                                            Pending
                                        </span>
                                    )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`px-2 py-1 text-xs rounded-full ${
                                        client.active !== false
                                            ? 'bg-green-100 text-green-800'
                                            : 'bg-gray-100 text-gray-800'
                                    }`}>
                                        {client.active !== false ? 'Active' : 'Inactive'}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-center">
                                    <div className="flex items-center justify-center gap-2">
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onView(client);
                                            }}
                                            className="action-button text-gray-600 hover:text-blue-600 transition"
                                            title="View Details"
                                        >
                                            <Eye className="w-4 h-4" />
                                        </button>
                                        {hasPermission && hasPermission('CLIENTS:UPDATE') && (
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onEdit(client);
                                            }}
                                            className="action-button text-gray-600 hover:text-indigo-600 transition"
                                            title="Edit Client"
                                        >
                                            <Edit2 className="w-4 h-4" />
                                        </button>
                                        )}
                                        {hasPermission && hasPermission('CLIENTS:DELETE') && (
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onDelete(client);
                                            }}
                                            className="action-button text-gray-600 hover:text-red-600 transition"
                                            title="Delete Client"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ClientsList;
